import Entity from "_framework/entities/entity";
import Guid from "_shared/guids/guid";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import Individual from "marketplace/individuals/individual";
import TeamProfile from "marketplace/team-profile/team-profile";
import TeamSchema, { TeamJSON, TeamParams } from "marketplace/teams/_schema/team-schema";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** A vendor team listed on the marketplace that can be referenced
 * when creating proposals.
 */
export default class Team extends Entity<Team, TeamJSON, TeamParams> {
  public static get EXAMPLE(): Team {
    return new Team({
      id: Guid.generate(),
      leader: Individual.EXAMPLE,
      profile: TeamProfile.EXAMPLE,
      isVisible: true,
      members: [Individual.EXAMPLE],
      associatedFirms: [CompanyProfile.EXAMPLE],
    });
  }

  public static create(values: TeamParams): Team {
    const parsed = TeamSchema.validate(values);
    return new Team(parsed);
  }

  public static fromJSON(json: TeamJSON): Team {
    const parsed = TeamSchema.deserialize(json);
    return new Team(parsed);
  }

  public static fromJSONArray(jsonArray: TeamJSON[]): Team[] {
    return jsonArray.map((json) => Team.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly leader: Individual;
  public readonly profile: TeamProfile;
  public readonly members: Individual[];
  public readonly associatedFirms: CompanyProfile[];
  public isVisible: boolean = false;

  public toWorkTeam(): VendorTeam {
    return new VendorTeam({
      leader: this.leader.toEntityMember(),
      members: this.members.map((m) => m.toEntityMember()),
      marketplaceId: this.id,
    });
  }

  public constructor(params: TeamParams) {
    super(params);
    this.id = params.id;
    this.leader = Individual.create(params.leader);
    this.profile = TeamProfile.create(params.profile);
    this.members = params.members.map(Individual.create);
    this.associatedFirms = params.associatedFirms.map(CompanyProfile.create);
    this.isVisible = params.isVisible;
  }

  public toggleVisibility = (): boolean => (this.isVisible = !this.isVisible);

  public async saveChanges(): Promise<Team> {
    throw new Error("Save functionality not implemented.");
  }

  public toJSON(): TeamJSON {
    return TeamSchema.serialize(this);
  }

  public valueOf(): TeamJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `Team: 
        Id: ${this.id.toString()}
        Leader: ${this.leader.toString()}
        Profile: ${this.profile.toString()}
        Members: [${this.members.map((m) => m.toString()).join(", ")}]
        Associated Firms: [${this.associatedFirms.map((f) => f.toString()).join(", ")}]
        Is Visible: ${this.isVisible}
      )`;
  }

  public isEqualTo(other: Team | null | undefined): boolean {
    return Boolean(
      other &&
        this.id.isEqualTo(other.id) &&
        this.leader.isEqualTo(other?.leader) &&
        this.profile.isEqualTo(other?.profile) &&
        this.members.length === other?.members.length &&
        this.members.every((member) => other.members.some((otherMember) => member.isEqualTo(otherMember))) &&
        this.associatedFirms.length === other?.associatedFirms.length &&
        this.associatedFirms.every((firm) => other.associatedFirms.some((otherFirm) => firm.isEqualTo(otherFirm)))
    );
  }
}
